import React, { useState } from 'react';
import { usePensionStore } from '../store/pensionStore.ts';

interface HeaderProps {
  onAddYear: () => void;
  onExport?: () => void;
  onImport?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onAddYear, onExport, onImport }) => {
  const { clearAllData } = usePensionStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      clearAllData();
      setIsMobileMenuOpen(false);
    }
  };

  const handleAddYear = () => {
    onAddYear();
    setIsMobileMenuOpen(false);
  };

  const handleExport = () => {
    onExport?.();
    setIsMobileMenuOpen(false);
  };

  const handleImport = () => {
    onImport?.();
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200 relative z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-base md:text-lg font-bold text-gray-900">
              🇬🇧 UK Pension Allowance Calculator
            </h1>

            {/* Desktop Menu */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={onAddYear}
                className="border border-blue-600 text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors min-h-[44px]"
              >
                Add Year
              </button>

              {onExport && (
                <button
                  onClick={onExport}
                  className="border border-green-600 text-green-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors min-h-[44px]"
                >
                  Export
                </button>
              )}

              {onImport && (
                <button
                  onClick={onImport}
                  className="border border-purple-600 text-purple-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-50 transition-colors min-h-[44px]"
                >
                  Import
                </button>
              )}

              <button
                onClick={handleClearAll}
                className="border border-red-600 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors min-h-[44px]"
              >
                Clear All
              </button>
            </div>

            {/* Mobile Hamburger Menu Button */}
            <div className="sm:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed top-[73px] right-4 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[200px] sm:hidden">
            <div className="py-2">
              <button
                onClick={handleAddYear}
                className="w-full px-4 py-3 text-left text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Year
              </button>

              {onExport && (
                <button
                  onClick={handleExport}
                  className="w-full px-4 py-3 text-left text-sm font-medium text-green-600 hover:bg-green-50 transition-colors flex items-center gap-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Data
                </button>
              )}

              {onImport && (
                <button
                  onClick={handleImport}
                  className="w-full px-4 py-3 text-left text-sm font-medium text-purple-600 hover:bg-purple-50 transition-colors flex items-center gap-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Import Data
                </button>
              )}

              <hr className="my-2 border-gray-200" />

              <button
                onClick={handleClearAll}
                className="w-full px-4 py-3 text-left text-sm font-medium text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Clear All Data
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};